<script lang="ts">
  import { onMount } from 'svelte';
  import { isBinned as isDocumentBinned } from '../lib/documentStatus';
  import { navigate } from 'svelte-routing';
  import { _, locale } from 'svelte-i18n';
  import { api, type Document, type ShareLinkDto } from '../lib/api';
  import { ApiError } from '../lib/api/errorHandler';
  import { permissionsStore, permissionsApi } from '../lib/permissions/PermissionsService';
  import { userSettings } from '../lib/stores/settingsStore';
  import { categoriesStore } from '../lib/stores/categoriesStore';
  import { formatDateTime, formatDate } from '../lib/settings';
  import {
    getLocalizedClassificationCategoryName,
    getLocalizedClassificationSubCategoryName,
  } from '../lib/categoriesLocalization';
  import { householdsStore } from '../lib/stores/householdsStore';
  import { followupsStore } from '../lib/stores/followupsStore';
  import { isFollowupOverdue, followupErrorMessage } from '../lib/followups';
  import type { Followup } from '../lib/api/followups';
  import {
    WARNING_FLAG_I18N_KEYS,
    recognizedWarningFlags,
    distinctSentenceFlags,
  } from '../lib/warningFlags';
  import { rankTagSuggestions } from '../lib/tagSuggest';
  import { isProcessableType } from '../lib/fileSupport';
  import Download from 'lucide-svelte/icons/download';
  import RefreshCw from 'lucide-svelte/icons/refresh-cw';
  import Trash2 from 'lucide-svelte/icons/trash-2';
  import RotateCcw from 'lucide-svelte/icons/rotate-ccw';
  import ChevronDown from 'lucide-svelte/icons/chevron-down';
  import ChevronRight from 'lucide-svelte/icons/chevron-right';
  import Tag from 'lucide-svelte/icons/tag';
  import Plus from 'lucide-svelte/icons/plus';
  import X from 'lucide-svelte/icons/x';
  import Users from 'lucide-svelte/icons/users';
  import FileText from 'lucide-svelte/icons/file-text';
  import Info from 'lucide-svelte/icons/info';
  import Layers from 'lucide-svelte/icons/layers';
  import Eye from 'lucide-svelte/icons/eye';
  import Home from 'lucide-svelte/icons/home';
  import Pencil from 'lucide-svelte/icons/pencil';
  import MessageSquarePlus from 'lucide-svelte/icons/message-square-plus';
  import FileSearch from 'lucide-svelte/icons/file-search';
  import AlertTriangle from 'lucide-svelte/icons/alert-triangle';
  import StickyNote from 'lucide-svelte/icons/sticky-note';
  import Share2 from 'lucide-svelte/icons/share-2';
  import Copy from 'lucide-svelte/icons/copy';
  import CheckCircle from 'lucide-svelte/icons/check-circle';
  import Link2 from 'lucide-svelte/icons/link-2';
  import Ban from 'lucide-svelte/icons/ban';
  import CalendarClock from 'lucide-svelte/icons/calendar-clock';
  import Sparkles from 'lucide-svelte/icons/sparkles';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import {
    Button,
    Card,
    Spinner,
    Badge,
    ConfidenceBadge,
    StatusBadge,
    Modal,
    ExtractedDataEditor,
    DateInput,
  } from '../lib/components/ui/index';
  import FeedbackModal from '../lib/components/FeedbackModal.svelte';
  import DocumentPreview from '../lib/components/DocumentPreview.svelte';
  import PreviewSeparator from '../lib/components/PreviewSeparator.svelte';
  import DocumentReferences from '../lib/components/DocumentReferences.svelte';
  import DocumentChatPanel from '../lib/components/DocumentChatPanel.svelte';
  import type { AvailableSkill, ChatMessage, ChatSessionSummary } from '../lib/api/chat';
  import { toastStore } from '../lib/stores/toastStore';

  export let id: string;

  let document: Document | null = null;
  let previewWidth = 600;
  let loading = true;
  let error: string | null = null;
  let isNotFound = false;
  let deleting = false;
  let reprocessing = false;
  let ocrExpanded = false;
  let extractedDataExpanded = false;
  // For User Tags
  let editableUserTags: string[] = [];
  let availableUserTags: string[] = [];
  let editingUserTags = false;
  let userTagsSaving = false;
  let userTagInput = '';

  // Classification edit state
  let editingClassification = false;
  let classificationSaving = false;
  let editableCategory = '';
  let editableSubCategory = '';

  // AssignedTo edit state
  let editingAssignedTo = false;
  let assignedToSaving = false;
  let editableAssignedTo: string[] = [];

  // Notes edit state
  let editingNotes = false;
  let notesSaving = false;
  let editableNotes = '';

  // Wiedervorlage edit state (Spec 086/E10-E13)
  let activeFollowup: Document['activeFollowup'] | null = null;
  let editingFollowup = false;
  let followupSaving = false;
  let followupCompleting = false;
  let followupDueDate = '';
  let followupSubject = '';
  let followupNote = '';

  // Extracted Data edit state
  let editingExtractedData = false;
  let extractedDataSaving = false;
  let editableExtractedValues: Record<string, string> = {};

  let canDeleteDocuments: boolean = false;
  let canReprocessDocuments: boolean = false;
  let showFeedbackModal = false;
  let restoring = false;
  let moveHouseholdId = '';
  let movingHousehold = false;

  // Share modal state (Spec 014 E7)
  let showShareModal = false;
  let shareVisibility: 'public' | 'authenticated' = 'public';
  let shareExpiryOption: '7' | '30' | 'custom' | string = '30';
  let shareCustomDate = '';
  let shareCreating = false;
  let shareError: string | null = null;
  let shareLinks: ShareLinkDto[] = [];
  let shareLinksLoading = false;
  let shareLinksError: string | null = null;
  let copiedLinkId: string | null = null;
  let revokingLinkId: string | null = null;

  async function openShareModal() {
    if (!document) return;
    showShareModal = true;
    shareError = null;
    shareLinks = [];
    shareLinksLoading = true;
    shareLinksError = null;
    try {
      shareLinks = await api.sharing.listShareLinks(document.id);
    } catch (err) {
      shareLinksError = err instanceof Error ? err.message : 'Error';
    } finally {
      shareLinksLoading = false;
    }
  }

  async function handleCreateShareLink() {
    if (!document) return;
    shareCreating = true;
    shareError = null;
    try {
      let expiresInDays: number | undefined;
      let expiresAt: string | undefined;
      if (shareExpiryOption === '7') {
        expiresInDays = 7;
      } else if (shareExpiryOption === 'custom' && shareCustomDate) {
        expiresAt = new Date(shareCustomDate + 'T23:59:59Z').toISOString();
      } else {
        expiresInDays = 30;
      }
      const created = await api.sharing.createShareLink(document.id, {
        visibility: shareVisibility,
        expiresInDays,
        expiresAt,
      });
      const shareUrl = api.sharing.getSharedDownloadUrl(created.token);
      await navigator.clipboard.writeText(shareUrl);
      toastStore.success($_('components.document_detail.share_copied'));
      shareLinks = [created, ...shareLinks];
    } catch (err) {
      shareError = err instanceof Error ? err.message : 'Error';
    } finally {
      shareCreating = false;
    }
  }

  async function handleCopyShareLink(link: ShareLinkDto) {
    const shareUrl = api.sharing.getSharedDownloadUrl(link.token);
    await navigator.clipboard.writeText(shareUrl);
    copiedLinkId = link.id;
    setTimeout(() => {
      copiedLinkId = null;
    }, 2000);
  }

  async function handleRevokeShareLink(link: ShareLinkDto) {
    if (!document) return;
    revokingLinkId = link.id;
    try {
      await api.sharing.revokeShareLink(document.id, link.id);
      shareLinks = shareLinks.filter((l) => l.id !== link.id);
    } catch (err) {
      shareLinksError = err instanceof Error ? err.message : 'Error';
    } finally {
      revokingLinkId = null;
    }
  }

  function resolveMemberName(memberId: string): string {
    for (const household of $householdsStore) {
      const member = household.members?.find((m) => m.id === memberId);
      if (member) return member.name;
    }
    return memberId;
  }

  $: ({ canDeleteDocuments, canReprocessDocuments } = $permissionsStore || {
    canDeleteDocuments: false,
    canReprocessDocuments: false,
  });

  // Spec 074/E6: a document in the bin loses its editing actions and gains a restore path —
  // the same wire signal (isBinned) already gates the feedback button.
  $: isBinned = isDocumentBinned(document);

  // Spec 065 E5/E6: system:warningFlags carries system-diagnosed fallback reasons
  // (comma-separated, ADR-037). Each recognized value gets a human-readable sentence;
  // unrecognized values are dropped rather than shown as a raw token. Shared with
  // UploadDocumentDetail.svelte via lib/warningFlags.ts (review F2).
  $: warningFlags = recognizedWarningFlags(
    document?.extractedData?.extractedValues?.['system:warningFlags'],
  );

  // Review L3: ONE banner, not one per reason. Classification confidence and the extraction
  // warning flags come from independent pipeline steps, so a document can trip both at once —
  // two separate {#if} blocks would then stack two visually identical accent boxes. The
  // /uiux-review's rule was "extend the existing banner's condition", and this is it.
  //
  // Review F1: a classification fallback always uses confidence=0.5 (DocumentClassifier
  // ClassifyWithFallback), so ai_classification_failed and hasLowConfidence would otherwise
  // always fire together — the same sentence twice. The warning-flag text already says
  // everything low_confidence_warning would add, so it wins.
  $: hasLowConfidence =
    !!document?.classification &&
    document.classification.confidence < 0.7 &&
    !document.classification.isManualClassified &&
    !warningFlags.includes('ai_classification_failed');

  async function loadDocument() {
    loading = true;
    error = null;
    isNotFound = false;
    ocrExpanded = false;
    extractedDataExpanded = false;
    editingUserTags = false;
    userTagInput = '';
    editingClassification = false;
    editingAssignedTo = false;

    try {
      // Ensure categories are loaded for localization
      await categoriesStore.ensureLoaded();

      document = await api.documents.getDocument(id);
      if (document) {
        editableUserTags = [...document.userTags]; // Initialize editable tags
        activeFollowup = document.activeFollowup ?? null;
        editingFollowup = false;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        isNotFound = true;
      } else {
        error = err instanceof Error ? err.message : $_('errors.generic');
      }
    } finally {
      loading = false;
    }
  }

  // Spec 113 E1/E2: no confirmation; the toast offers "Undo" which restores and returns here.
  async function handleDelete() {
    if (!document || deleting) return;

    const documentId = document.id;
    deleting = true;
    try {
      await api.documents.deleteDocument(documentId);
      toastStore.success($_('components.document_detail.deleted_success'), undefined, {
        action: { label: $_('messages.undo'), onClick: () => undoDelete(documentId) },
      });
      navigate('/browse');
    } catch {
      // handleApiError already showed toast + redirected if 401
    } finally {
      deleting = false;
    }
  }

  async function undoDelete(documentId: string) {
    try {
      await api.documents.restoreDocuments([documentId]);
      navigate(`/documents/${documentId}`);
    } catch {
      toastStore.error($_('messages.undo_failed'));
    }
  }

  // Spec 074/E6c: after restoring, the user STAYS on the detail page — the banner disappears
  // and the editing actions reappear, instead of bouncing back to the bin.
  async function handleRestore() {
    if (!document) return;

    restoring = true;
    try {
      await api.documents.restoreDocuments([document.id]);
      toastStore.success($_('components.document_detail.restore_success'));
      await loadDocument();
    } catch {
      toastStore.error($_('components.document_detail.restore_error'));
    } finally {
      restoring = false;
    }
  }

  // Spec 123: `attachment` makes the browser save the file, so the page stays put (no popup tab).
  let opening = false;
  async function handleDownload() {
    if (!document || opening) return;
    opening = true;
    try {
      const url = await api.documents.getDownloadSignedUrl(document.id, 'attachment');
      window.location.assign(url);
    } catch {
      toastStore.error($_('components.document_detail.download_error'));
    } finally {
      opening = false;
    }
  }

  // User Tags Functions
  function handleEditUserTags() {
    editingUserTags = true;
    userTagInput = '';
    loadAvailableUserTags();
  }

  // Autocomplete (#238): distinct tags of the document's household, suggested while typing.
  async function loadAvailableUserTags() {
    try {
      const tags = await api.tags.getUserTags(document?.householdId);
      availableUserTags = tags.map((t) => t.tag);
    } catch {
      availableUserTags = []; // suggestions are best-effort
    }
  }

  $: tagSuggestions = editingUserTags
    ? rankTagSuggestions(availableUserTags, userTagInput, editableUserTags)
    : [];

  function handleAddUserTag() {
    addUserTag(userTagInput);
  }

  function addUserTag(value: string) {
    const tag = value.trim();
    if (tag && !editableUserTags.includes(tag)) {
      editableUserTags = [...editableUserTags, tag];
    }
    userTagInput = '';
  }

  function handleRemoveUserTag(tagToRemove: string) {
    editableUserTags = editableUserTags.filter((tag) => tag !== tagToRemove);
  }

  async function handleSaveUserTags() {
    if (!document) return;

    // Ensure any tag currently in the input field is added before saving
    handleAddUserTag();

    userTagsSaving = true;
    try {
      await api.tags.updateUserTags(document.id, editableUserTags);
      document.userTags = [...editableUserTags];
      editingUserTags = false;
      toastStore.success($_('components.document_detail.tags_updated'));
    } catch {
      // handleApiError already showed toast + redirected if 401
    } finally {
      userTagsSaving = false;
    }
  }

  function handleCancelEditUserTags() {
    if (document) {
      editableUserTags = [...document.userTags];
    }
    editingUserTags = false;
  }

  // Notes edit functions
  function handleEditNotes() {
    editableNotes = document?.notes || '';
    editingNotes = true;
  }

  function handleCancelEditNotes() {
    editingNotes = false;
  }

  // Wiedervorlage functions (Spec 086/E10-E13)

  // E11: subject prefill fallback chain — subject, then system:vendor, then displayName (S15/S16).
  function followupSubjectFallback(): string {
    if (!document) return '';
    const values = document.extractedData?.extractedValues ?? {};
    return values['subject'] || values['system:vendor'] || document.displayName;
  }

  function handleSetFollowup() {
    followupDueDate = '';
    followupSubject = followupSubjectFallback();
    followupNote = '';
    editingFollowup = true;
  }

  function handleEditFollowup() {
    if (!activeFollowup) return;
    followupDueDate = activeFollowup.dueDate;
    followupSubject = activeFollowup.subject;
    followupNote = activeFollowup.note ?? '';
    editingFollowup = true;
  }

  function handleCancelEditFollowup() {
    editingFollowup = false;
  }

  async function handleSaveFollowup() {
    if (!document || !followupDueDate) return;

    followupSaving = true;
    try {
      let saved: Followup;
      if (activeFollowup) {
        saved = await api.followups.update(activeFollowup.id, {
          dueDate: followupDueDate,
          subject: followupSubject,
          note: followupNote || undefined,
        });
        // F4: without this, the Topbar badge/`/followups` list stay on the pre-edit dueDate
        // (e.g. still "overdue") until the next full reload.
        followupsStore.updateLocal(saved);
      } else {
        saved = await api.followups.create({
          documentId: document.id,
          dueDate: followupDueDate,
          subject: followupSubject,
          note: followupNote || undefined,
        });
        followupsStore.add(saved);
      }
      activeFollowup = {
        id: saved.id,
        dueDate: saved.dueDate,
        subject: saved.subject,
        note: saved.note ?? undefined,
      };
      editingFollowup = false;
      toastStore.success($_('pages.followups.save_success'));
    } catch {
      // F2: create/update do NOT suppress the global handler (their errorCodes are in
      // `errorHandler.ts`'s LOCALIZED_ERROR_CODES) — it already showed the one translated toast.
      // Only the local state matters here: `editingFollowup` stays true (unchanged above), so the
      // form stays open for another try (S18).
    } finally {
      followupSaving = false;
    }
  }

  async function handleRemoveFollowup() {
    if (!activeFollowup) return;

    followupSaving = true;
    try {
      await api.followups.remove(activeFollowup.id);
      followupsStore.removeLocal(activeFollowup.id);
      activeFollowup = null;
      editingFollowup = false;
      toastStore.success($_('pages.followups.remove_success'));
    } catch {
      // F2: `remove()` doesn't suppress the global handler either — same reasoning as above.
    } finally {
      followupSaving = false;
    }
  }

  // E13: after completing from the card, it reverts to "Wiedervorlage setzen" — the API only
  // returns the active followup (085/E13), the completed one now lives under /followups?state=completed.
  // F3: the checkbox is a one-way binding (no `checked` prop at all — always unchecked at rest),
  // so a failed complete() must be reverted by hand or it stays visually checked.
  // E5: undo always reopens (store + header count); the card is only touched while the same
  // document is still shown.
  async function undoCompleteFollowup(followupId: string, documentId: string) {
    try {
      const reopened = await followupsStore.reopen(followupId);
      if (id === documentId) {
        activeFollowup = {
          id: reopened.id,
          dueDate: reopened.dueDate,
          subject: reopened.subject,
          note: reopened.note ?? undefined,
        };
      }
      toastStore.success($_('pages.followups.reopen_success'));
    } catch (err) {
      const errorCode = (err as { errorCode?: string } | undefined)?.errorCode;
      toastStore.error(followupErrorMessage(errorCode, $_));
    }
  }

  async function handleCompleteFollowup(e: Event) {
    if (!activeFollowup) return;
    const checkbox = e.target as HTMLInputElement;

    followupCompleting = true;
    try {
      const completedId = activeFollowup.id;
      const documentId = id;
      await followupsStore.complete(completedId);
      activeFollowup = null;
      toastStore.success($_('pages.followups.complete_success'), undefined, {
        action: {
          label: $_('pages.followups.undo'),
          onClick: () => undoCompleteFollowup(completedId, documentId),
        },
      });
    } catch (err) {
      checkbox.checked = false;
      const errorCode = (err as { errorCode?: string } | undefined)?.errorCode;
      toastStore.error(followupErrorMessage(errorCode, $_));
    } finally {
      followupCompleting = false;
    }
  }

  async function handleSaveNotes() {
    if (!document) return;

    notesSaving = true;
    try {
      await api.documents.updateDocumentMetadata(document.id, { notes: editableNotes || '' });
      document.notes = editableNotes.trim() || undefined;
      editingNotes = false;
      toastStore.success($_('components.document_detail.notes_updated'));
    } catch {
      // handleApiError already showed toast
    } finally {
      notesSaving = false;
    }
  }

  // Classification edit functions
  function handleEditClassification() {
    if (!document) return;
    editableCategory = document.classification?.category || '';
    editableSubCategory = document.classification?.subCategory || '';
    editingClassification = true;
  }

  function handleCancelEditClassification() {
    editingClassification = false;
  }

  $: availableSubCategories =
    $categoriesStore.find((c) => c.name === editableCategory)?.subCategories || [];

  // Reset subCategory when category changes
  function handleCategoryChange() {
    editableSubCategory = '';
  }

  async function handleSaveClassification() {
    if (!document || !editableCategory || !editableSubCategory) return;

    classificationSaving = true;
    try {
      await api.documents.updateDocumentClassification(
        document.id,
        editableCategory,
        editableSubCategory,
      );
      document.classification = {
        category: editableCategory,
        subCategory: editableSubCategory,
        confidence: 1.0,
        isManualClassified: true,
      };
      editingClassification = false;
      toastStore.success($_('components.document_detail.classification_updated'));
    } catch {
      // handleApiError already showed toast
    } finally {
      classificationSaving = false;
    }
  }

  // AssignedTo edit functions
  function handleEditAssignedTo() {
    if (!document) return;
    editableAssignedTo = [...document.assignedTo];
    editingAssignedTo = true;
  }

  function handleCancelEditAssignedTo() {
    editingAssignedTo = false;
  }

  function toggleMemberAssignment(memberId: string) {
    if (editableAssignedTo.includes(memberId)) {
      editableAssignedTo = editableAssignedTo.filter((id) => id !== memberId);
    } else {
      editableAssignedTo = [...editableAssignedTo, memberId];
    }
  }

  async function handleSaveAssignedTo() {
    if (!document) return;

    assignedToSaving = true;
    try {
      await api.documents.updateDocumentAssignedTo(document.id, editableAssignedTo);
      document.assignedTo = [...editableAssignedTo];
      editingAssignedTo = false;
      toastStore.success($_('components.document_detail.assigned_to_updated'));
    } catch {
      // handleApiError already showed toast
    } finally {
      assignedToSaving = false;
    }
  }

  // Extracted data edit functions
  function handleEditExtractedData() {
    if (!document?.extractedData) return;
    editableExtractedValues = { ...document.extractedData.extractedValues };
    editingExtractedData = true;
    extractedDataExpanded = true;
  }

  function handleCancelEditExtractedData() {
    editingExtractedData = false;
  }

  async function handleSaveExtractedData() {
    if (!document) return;

    extractedDataSaving = true;
    try {
      await api.documents.updateDocumentMetadata(document.id, {
        extractedValues: editableExtractedValues,
      });

      if (document.extractedData) {
        document.extractedData.extractedValues = { ...editableExtractedValues };
      }

      editingExtractedData = false;
      toastStore.success($_('upload.detail.save_success'));
    } catch {
      // Error handled by API client
    } finally {
      extractedDataSaving = false;
    }
  }

  $: currentHouseholdMembers =
    $householdsStore.find((h) => h.id === document?.householdId)?.members || [];

  async function handleReprocess() {
    if (!document) return;

    reprocessing = true;
    try {
      await api.documents.reprocessDocument(document.id);
      toastStore.success($_('components.document_detail.reprocess_initiated'));
      navigate('/documents/' + document.id);
    } catch {
      // handleApiError already showed toast + redirected if 401
    } finally {
      reprocessing = false;
    }
  }

  async function handleMoveHousehold() {
    if (!document || !moveHouseholdId || moveHouseholdId === document.householdId) return;
    movingHousehold = true;
    try {
      await api.households.moveDocumentToHousehold(document.id, moveHouseholdId);
      document.householdId = moveHouseholdId;
      toastStore.success($_('household.move_success'));
    } catch {
      // handleApiError already showed toast + redirected if 401
    } finally {
      movingHousehold = false;
    }
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  let prevId: string | null = null;

  // --- Chat at the document (Spec 128) ---
  // The button is there from the first render (no layout shift) and only goes away when the backend
  // says the chat is switched off ('disabled'); any other load failure shows in the window ('error').
  type ChatState = 'loading' | 'ready' | 'error' | 'disabled';
  let chatState: ChatState = 'loading';
  // The route may carry a slug; the chat is keyed by the document UUID (ADR-065), so it loads once the document is known.
  let chatLoadedFor: string | null = null;
  let chatMessages: ChatMessage[] = [];
  let chatSkills: AvailableSkill[] = [];
  // Spec 143: the shown session (`null` = new, not yet started) and the session list; kept here so closing the window forgets nothing.
  let chatSessionId: string | null = null;
  let chatSessions: ChatSessionSummary[] = [];
  let chatOpen = new URLSearchParams(window.location.search).get('chat') === '1';
  let chatButtonHost: HTMLElement | undefined;

  $: chatLockedReason = isBinned ? $_('chat.disabledInBin') : null;
  $: chatContextValues = Object.entries(document?.extractedData?.extractedValues ?? {}).filter(
    ([name]) => !name.startsWith('system:'),
  );

  async function loadChatHistory(documentId: string) {
    chatState = 'loading';
    chatMessages = [];
    chatSkills = [];
    chatSessionId = null;
    chatSessions = [];
    try {
      // The list is a nicety next to the history: its failure must not decide about the chat state.
      const sessionsRequest = api.chat
        .getSessions(documentId)
        .catch(() => [] as ChatSessionSummary[]);
      const history = await api.chat.getHistory(documentId);
      const sessions = await sessionsRequest;
      if (documentId !== chatLoadedFor) return;
      chatMessages = history.messages;
      chatSkills = history.availableSkills ?? [];
      chatSessionId = history.sessionId ?? null;
      chatSessions = sessions;
      chatState = 'ready';
    } catch (error) {
      // No toast: the chat is a secondary feature. Switched off (E11, S15) hides the button, anything
      // else is shown in the window, where the user can reload.
      if (documentId !== chatLoadedFor) return;
      chatState =
        error instanceof ApiError && error.errorCode === 'chat.disabled' ? 'disabled' : 'error';
    }
  }

  // Skill names come localized from the server (Spec 129 S16): a language switch refreshes only the skills,
  // the conversation and a draft stay untouched.
  let chatSkillsLocale = $locale;
  $: if ($locale !== chatSkillsLocale) {
    chatSkillsLocale = $locale;
    refreshChatSkills();
  }

  async function refreshChatSkills() {
    const documentId = chatLoadedFor;
    if (!documentId || chatState !== 'ready') return;
    try {
      const history = await api.chat.getHistory(documentId);
      if (documentId === chatLoadedFor) chatSkills = history.availableSkills ?? [];
    } catch {
      // keep the skills shown; the language refresh is a nicety
    }
  }

  // The open state lives in the URL (`?chat=1`) so a reload or a shared link keeps the window open (E19f).
  function setChatUrlFlag(open: boolean) {
    const url = new URL(window.location.href);
    if (open) url.searchParams.set('chat', '1');
    else url.searchParams.delete('chat');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }

  function openChat() {
    // S6: a "new session" that got no message is forgotten with the window; the last used session shows again.
    if (chatSessionId === null && chatSessions.length > 0 && chatLoadedFor)
      loadChatHistory(chatLoadedFor);
    chatOpen = true;
    setChatUrlFlag(true);
  }

  // The chat creates the follow-up itself: the header counter reloads the open ones (a replace also removed one).
  function onChatFollowupCreated(e: CustomEvent<NonNullable<Document['activeFollowup']>>) {
    activeFollowup = e.detail;
    followupsStore.refresh().catch(() => {});
  }

  function closeChat() {
    // Focus first: once the window is gone there is no element left to hold it.
    chatButtonHost?.querySelector('button')?.focus();
    chatOpen = false;
    setChatUrlFlag(false);
  }

  // Re-load document only when route parameter (id) changes
  $: if (id && id !== prevId) {
    prevId = id;
    loadDocument();
  }

  $: if (document?.id && document.id !== chatLoadedFor) {
    chatLoadedFor = document.id;
    loadChatHistory(document.id);
  }
</script>

{#if loading}
  <div class="flex justify-center py-16">
    <Spinner size="lg" label={$_('messages.loading')} />
  </div>
{:else if isNotFound}
  <div class="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div class="mb-6 p-6 bg-accent/10 rounded-full text-accent">
      <FileSearch class="w-16 h-16" strokeWidth={1.5} />
    </div>
    <h1 class="text-2xl font-semibold text-text mb-2">
      {$_('pages.error.document_not_found_title')}
    </h1>
    <p class="text-text-secondary mb-8 max-w-md">{$_('pages.error.document_not_found_message')}</p>
    <Button variant="primary" on:click={() => navigate('/browse')}>
      {$_('pages.browse.title')}
    </Button>
  </div>
{:else if error}
  <div class="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div class="mb-6 p-6 bg-error/10 rounded-full">
      <AlertTriangle class="w-16 h-16 text-error" strokeWidth={1.5} />
    </div>
    <h1 class="text-2xl font-semibold text-text mb-2">{$_('pages.error.server_error_title')}</h1>
    <p class="text-text-secondary mb-4 max-w-md">{$_('pages.error.server_error_message')}</p>
    <div
      class="bg-bg-alt border border-border p-3 rounded-lg mb-8 font-mono text-xs text-text-muted break-all max-w-lg"
    >
      {error}
    </div>
    <div class="flex gap-3">
      <Button variant="primary" on:click={loadDocument}>
        <RefreshCw class="w-4 h-4" strokeWidth={1.5} />
        {$_('pages.error.retry')}
      </Button>
      <Button variant="secondary" on:click={() => navigate('/browse')}
        >{$_('pages.browse.title')}</Button
      >
    </div>
  </div>
{:else if document}
  <!-- lg+: the page fills the content area (--page-h from AppShell); title and buttons stay on top,
       only the metadata column scrolls beside the preview. Stacked it is one scrolling page. -->
  <div class="lg:flex lg:flex-col lg:h-[var(--page-h)]">
    <div data-testid="doc-detail-title" class="contents">
      <PageHeader
        title={document.fileName}
        breadcrumbs={[
          { label: $_('nav.dashboard'), href: '/' },
          isBinned
            ? { label: $_('nav.bin'), href: '/bin' }
            : { label: $_('pages.browse.title'), href: '/browse' },
          { label: document.fileName },
        ]}
      >
        <svelte:fragment slot="actions">
          <div class="flex gap-2 flex-wrap">
            <Button variant="primary" size="sm" disabled={opening} on:click={handleDownload}>
              <Download class="w-4 h-4" strokeWidth={1.5} />
              {$_('components.document_detail.download')}
            </Button>
            {#if chatState !== 'disabled'}
              <span
                bind:this={chatButtonHost}
                class="inline-flex"
                title={chatLockedReason ?? undefined}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={chatLockedReason !== null}
                  title={chatLockedReason ?? undefined}
                  on:click={openChat}
                  data-testid="chat-open-button"
                >
                  <Sparkles class="w-4 h-4" strokeWidth={1.5} />
                  {$_('chat.openButton')}
                </Button>
              </span>
            {/if}
            {#if !isBinned}
              <Button variant="secondary" size="sm" on:click={openShareModal}>
                <Share2 class="w-4 h-4" strokeWidth={1.5} />
                {$_('components.document_detail.share_action')}
              </Button>
            {/if}
            <Button variant="secondary" size="sm" on:click={loadDocument}>
              <RefreshCw class="w-4 h-4" strokeWidth={1.5} />
              {$_('components.document_detail.refresh')}
            </Button>
            {#if !isBinned}
              <Button
                variant="secondary"
                size="sm"
                on:click={() => (showFeedbackModal = true)}
                data-testid="feedback-open-button"
              >
                <MessageSquarePlus class="w-4 h-4" strokeWidth={1.5} />
                {$_('components.document_detail.feedback_button')}
              </Button>
            {/if}
            {#if canReprocessDocuments && !isBinned}
              <Button
                variant="secondary"
                size="sm"
                disabled={reprocessing}
                on:click={handleReprocess}
                data-testid="doc-detail-reprocess-button"
              >
                <RotateCcw class="w-4 h-4" strokeWidth={1.5} />
                {reprocessing
                  ? $_('components.document_detail.reprocessing')
                  : $_('components.document_detail.reprocess')}
              </Button>
            {/if}
            {#if canDeleteDocuments && !isBinned}
              <Button variant="danger" size="sm" disabled={deleting} on:click={handleDelete}>
                <Trash2 class="w-4 h-4" strokeWidth={1.5} />
                {$_('components.document_detail.delete')}
              </Button>
            {/if}
          </div>
        </svelte:fragment>
      </PageHeader>
    </div>

    {#if isBinned}
      <div
        data-testid="doc-detail-bin-banner"
        class="mb-6 p-4 bg-bg-alt border border-border rounded-lg flex items-center justify-between gap-3 flex-wrap"
      >
        <p class="text-sm text-text-secondary">
          {$_('components.document_detail.bin_banner_message', {
            values: {
              date: document.deletedAt ? formatDate(document.deletedAt, $userSettings) : '—',
            },
          })}
        </p>
        <Button
          variant="primary"
          size="sm"
          disabled={restoring}
          on:click={handleRestore}
          data-testid="doc-detail-restore-button"
        >
          <RotateCcw class="w-4 h-4" strokeWidth={1.5} />
          {$_('components.document_detail.restore_button')}
        </Button>
      </div>
    {/if}

    {#if hasLowConfidence || warningFlags.length > 0}
      <div
        class="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-lg flex items-start gap-3"
        data-testid={warningFlags.length > 0 ? 'doc-detail-ai-warning-banner' : undefined}
      >
        <AlertTriangle class="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
        <div class="flex flex-col gap-1">
          {#if hasLowConfidence}
            <p class="text-sm font-medium text-text">
              {$_('pages.error.low_confidence_warning')}
            </p>
          {/if}
          {#each distinctSentenceFlags(warningFlags, $_) as flag (flag)}
            <p
              class="text-sm font-medium text-text"
              data-testid={`doc-detail-ai-warning-reason-${flag}`}
            >
              {$_(WARNING_FLAG_I18N_KEYS[flag])}
            </p>
          {/each}
        </div>
      </div>
    {/if}

    {#if document && !isProcessableType(document.mimeType)}
      <div class="mb-6 p-4 bg-bg-alt border border-border rounded-lg flex items-center gap-3">
        <Info class="w-5 h-5 text-text-muted shrink-0" strokeWidth={1.5} />
        <p class="text-sm text-text-secondary">
          {$_('components.document_detail.unprocessed_notice')}
        </p>
      </div>
    {/if}

    <div class="flex flex-col lg:flex-row gap-6 lg:gap-0 lg:flex-1 lg:min-h-0">
      <!-- Metadata & Actions -->
      <div class="flex flex-col gap-6 flex-1 min-w-0 lg:pr-3 lg:overflow-y-auto">
        <!-- Wiedervorlage (Spec 086/E10-E13, E15: first card — the jump from /followups lands here) -->
        <div data-testid="doc-detail-followup-card">
          <Card>
            <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
              <CalendarClock class="w-4 h-4 text-accent" strokeWidth={1.5} />
              {$_('pages.followups.card_title')}
            </h2>
            {#if editingFollowup}
              <div class="flex flex-col gap-3">
                <div>
                  <label
                    for="doc-detail-followup-date-input"
                    class="block text-xs font-semibold text-text-muted mb-1"
                  >
                    {$_('pages.followups.date_label')}
                  </label>
                  <DateInput
                    id="doc-detail-followup-date-input"
                    data-testid="doc-detail-followup-date-input"
                    aria-label={$_('pages.followups.date_label')}
                    bind:value={followupDueDate}
                  />
                </div>
                <input
                  type="text"
                  data-testid="doc-detail-followup-subject-input"
                  bind:value={followupSubject}
                  placeholder={$_('pages.followups.subject_placeholder')}
                  aria-label={$_('pages.followups.subject_placeholder')}
                  maxlength="200"
                  class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                />
                <textarea
                  data-testid="doc-detail-followup-note-textarea"
                  bind:value={followupNote}
                  rows="2"
                  maxlength="2000"
                  placeholder={$_('pages.followups.note_placeholder')}
                  class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 resize-y"
                ></textarea>
                {#if !followupDueDate}
                  <p class="text-xs text-text-muted -mt-2">
                    {$_('pages.followups.date_required_hint')}
                  </p>
                {/if}
                <div class="flex gap-2 items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="doc-detail-followup-save"
                    disabled={followupSaving || !followupDueDate}
                    title={!followupDueDate ? $_('pages.followups.date_required_hint') : undefined}
                    on:click={handleSaveFollowup}
                  >
                    {followupSaving ? $_('messages.saving') : $_('messages.save')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="doc-detail-followup-cancel"
                    disabled={followupSaving}
                    on:click={handleCancelEditFollowup}
                  >
                    {$_('pages.settings.cancel')}
                  </Button>
                  {#if activeFollowup}
                    <div class="ml-auto">
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-error"
                        data-testid="doc-detail-followup-remove"
                        disabled={followupSaving}
                        on:click={handleRemoveFollowup}
                      >
                        {$_('pages.followups.remove_button')}
                      </Button>
                    </div>
                  {/if}
                </div>
              </div>
            {:else if activeFollowup}
              <div class="flex items-start gap-2">
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm {isFollowupOverdue(activeFollowup.dueDate)
                      ? 'text-error font-medium'
                      : 'text-text'}"
                  >
                    {formatDate(activeFollowup.dueDate, $userSettings)}
                    {#if isFollowupOverdue(activeFollowup.dueDate)}
                      · {$_('pages.followups.overdue_label')}
                    {/if}
                  </p>
                  <p class="text-sm font-medium text-text">{activeFollowup.subject}</p>
                  {#if activeFollowup.note}
                    <p class="text-sm text-text-secondary whitespace-pre-wrap">
                      {activeFollowup.note}
                    </p>
                  {/if}
                </div>
                {#if !isBinned}
                  <label class="flex items-center gap-1 text-xs text-text-muted cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="doc-detail-followup-checkbox"
                      disabled={followupCompleting}
                      on:change={handleCompleteFollowup}
                      aria-label={$_('pages.followups.mark_done_label', {
                        values: { subject: activeFollowup.subject },
                      })}
                      class="accent-accent cursor-pointer"
                    />
                    {$_('pages.followups.done_label')}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="doc-detail-followup-edit-button"
                    aria-label={$_('pages.followups.edit_button')}
                    on:click={handleEditFollowup}
                  >
                    <Pencil class="w-3 h-3" strokeWidth={1.5} />
                  </Button>
                {/if}
              </div>
            {:else}
              <div class="flex items-center gap-2">
                <p class="text-sm text-text-muted italic flex-1">
                  {$_('pages.followups.none_set')}
                </p>
                {#if !isBinned}
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="doc-detail-followup-set-button"
                    on:click={handleSetFollowup}
                  >
                    {$_('pages.followups.set_button')}
                  </Button>
                {/if}
              </div>
            {/if}
          </Card>
        </div>

        <!-- File Information -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <Info class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('components.document_detail.file_information')}
          </h2>
          <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt class="text-xs font-semibold text-text-muted">ID</dt>
            <dd class="text-sm font-mono text-text-muted break-all">{document.id}</dd>

            <dt class="text-xs font-semibold text-text-muted">
              {$_('components.document_detail.file_size')}
            </dt>
            <dd class="text-sm text-text">{formatFileSize(document.fileSize)}</dd>

            <dt class="text-xs font-semibold text-text-muted">
              {$_('components.document_detail.mime_type')}
            </dt>
            <dd class="text-sm text-text">{document.mimeType}</dd>

            <dt class="text-xs font-semibold text-text-muted">
              {$_('components.document_detail.uploaded_at')}
            </dt>
            <dd class="text-sm text-text">{formatDateTime(document.uploadedAt, $userSettings)}</dd>

            {#if document.referenceDate}
              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_table.column_date_reference')}
              </dt>
              <dd class="text-sm text-text font-medium text-accent">
                {formatDateTime(document.referenceDate, $userSettings)}
              </dd>
            {/if}

            {#if document.retentionYears}
              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.retention_years')}
              </dt>
              <dd class="text-sm text-text">
                {$_('components.document_detail.retention_years_value', {
                  values: { years: document.retentionYears },
                })}
              </dd>

              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.retain_until')}
              </dt>
              <dd class="text-sm text-text">
                {#if document.retainUntil}
                  {formatDateTime(document.retainUntil, $userSettings)}
                {:else}
                  {$_('components.document_detail.retain_until_no_date')}
                {/if}
              </dd>
            {:else if document.classification}
              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.retention_years')}
              </dt>
              <dd class="text-sm text-text-muted">
                {$_('components.document_detail.retention_years_none')}
              </dd>
            {/if}

            <dt class="text-xs font-semibold text-text-muted">
              {$_('components.document_detail.uploaded_by')}
            </dt>
            <dd class="text-sm text-text">{document.uploadedBy}</dd>

            <dt class="text-xs font-semibold text-text-muted">
              {$_('components.document_detail.status')}
            </dt>
            <dd><StatusBadge status={document.status} /></dd>

            {#if document.slug}
              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.slug')}
              </dt>
              <dd class="text-sm font-mono text-text-muted break-all">{document.slug}</dd>
            {/if}
          </dl>
        </Card>

        <!-- Household -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <Home class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('household.move_household')}
          </h2>
          <p class="text-sm text-text-secondary mb-2">
            {$_('household.switcher_label')}:
            <strong class="text-text"
              >{$householdsStore.find((h) => h.id === document?.householdId)?.name ||
                document.householdId}</strong
            >
          </p>
          {#if $householdsStore.length > 1}
            <div class="flex gap-2 items-center">
              <select
                bind:value={moveHouseholdId}
                class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
              >
                <option value="">-- {$_('household.move_household_label')} --</option>
                {#each $householdsStore.filter((h) => h.id !== document?.householdId) as h (h.id)}
                  <option value={h.id}>{h.name || h.id} ({$_(`household.type.${h.type}`)})</option>
                {/each}
              </select>
              <Button
                variant="secondary"
                size="sm"
                disabled={movingHousehold || !moveHouseholdId}
                on:click={handleMoveHousehold}
              >
                {$_('household.move_button')}
              </Button>
            </div>
          {/if}
        </Card>

        <!-- Classification -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <Layers class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('components.document_detail.classification')}
          </h2>
          {#if editingClassification}
            <div class="flex flex-col gap-3">
              <div>
                <label class="text-xs font-semibold text-text-muted block mb-1" for="edit-category"
                  >{$_('components.document_detail.category')}</label
                >
                <select
                  id="edit-category"
                  data-testid="doc-detail-category-select"
                  bind:value={editableCategory}
                  on:change={handleCategoryChange}
                  class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                >
                  <option value="">{$_('components.document_detail.select_category')}</option>
                  {#each $categoriesStore as cat (cat.name)}
                    <option value={cat.name}
                      >{getLocalizedClassificationCategoryName(cat.name, $locale)}</option
                    >
                  {/each}
                </select>
              </div>
              <div>
                <label
                  class="text-xs font-semibold text-text-muted block mb-1"
                  for="edit-subcategory">{$_('components.document_detail.subcategory')}</label
                >
                <select
                  id="edit-subcategory"
                  data-testid="doc-detail-subcategory-select"
                  bind:value={editableSubCategory}
                  disabled={!editableCategory}
                  class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-50"
                >
                  <option value="">{$_('components.document_detail.select_subcategory')}</option>
                  {#each availableSubCategories as sub (sub.name)}
                    <option value={sub.name}
                      >{getLocalizedClassificationSubCategoryName(
                        editableCategory,
                        sub.name,
                        $locale,
                      )}</option
                    >
                  {/each}
                </select>
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={classificationSaving || !editableCategory || !editableSubCategory}
                  on:click={handleSaveClassification}
                  data-testid="doc-detail-save-button"
                >
                  {classificationSaving ? $_('messages.saving') : $_('messages.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={classificationSaving}
                  on:click={handleCancelEditClassification}
                >
                  {$_('pages.settings.cancel')}
                </Button>
              </div>
            </div>
          {:else if document.classification}
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.category')}
              </dt>
              <dd data-testid="doc-detail-category" class="text-sm text-text">
                {getLocalizedClassificationCategoryName(document.classification.category, $locale)}
              </dd>

              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.subcategory')}
              </dt>
              <dd data-testid="doc-detail-subcategory" class="text-sm text-text">
                {getLocalizedClassificationSubCategoryName(
                  document.classification.category,
                  document.classification.subCategory,
                  $locale,
                )}
              </dd>

              <dt class="text-xs font-semibold text-text-muted">
                {$_('components.document_detail.confidence')}
              </dt>
              <dd data-testid="doc-detail-confidence">
                <ConfidenceBadge
                  score={document.classification.confidence}
                  isManualClassified={document.classification.isManualClassified ?? false}
                />
              </dd>
            </dl>
            {#if !isBinned}
              <Button
                variant="ghost"
                size="sm"
                on:click={handleEditClassification}
                data-testid="doc-detail-edit-button"
              >
                {$_('components.document_detail.edit_classification')}
              </Button>
            {/if}
          {:else}
            <p class="text-sm text-text-muted italic mb-2">—</p>
            {#if !isBinned}
              <Button variant="ghost" size="sm" on:click={handleEditClassification}>
                {$_('components.document_detail.edit_classification')}
              </Button>
            {/if}
          {/if}
        </Card>

        <!-- System Tags -->
        {#if document.systemTags.length > 0}
          <Card>
            <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
              <Tag class="w-4 h-4 text-accent" strokeWidth={1.5} />
              {$_('components.document_detail.system_tags')}
            </h2>
            <div class="flex flex-wrap gap-1.5">
              {#each document.systemTags as tag (tag)}
                <Badge variant="default">{tag}</Badge>
              {/each}
            </div>
          </Card>
        {/if}

        <!-- User Tags -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <Tag class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('components.document_detail.user_tags')}
          </h2>
          {#if editingUserTags}
            <div class="flex flex-col gap-3">
              <div class="flex flex-wrap gap-1.5">
                {#each editableUserTags as tag (tag)}
                  <Badge variant="accent" removable on:remove={() => handleRemoveUserTag(tag)}>
                    {tag}
                  </Badge>
                {/each}
              </div>
              <div class="flex flex-col gap-1">
                <div class="flex gap-2">
                  <input
                    type="text"
                    bind:value={userTagInput}
                    on:keydown={(e) => {
                      if (e.key === 'Enter') handleAddUserTag();
                    }}
                    placeholder={$_('components.document_detail.add_new_tag')}
                    class="flex-1 px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                  />
                  <button
                    on:click={handleAddUserTag}
                    type="button"
                    aria-label={$_('components.document_detail.add_tag_aria')}
                    title={$_('components.document_detail.add_tag_aria')}
                    class="inline-flex items-center justify-center min-w-8 min-h-8 px-2 text-text-muted hover:text-accent hover:bg-bg-alt rounded transition-colors"
                  >
                    <Plus class="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                {#if tagSuggestions.length > 0}
                  <ul class="flex flex-wrap gap-1.5 mt-1" data-testid="tag-suggestions">
                    {#each tagSuggestions as suggestion (suggestion)}
                      <li>
                        <button
                          type="button"
                          on:click={() => addUserTag(suggestion)}
                          class="px-2 py-0.5 text-xs rounded-full border border-border bg-surface-hover text-text-secondary hover:border-accent hover:text-accent transition-colors"
                        >
                          {suggestion}
                        </button>
                      </li>
                    {/each}
                  </ul>
                {/if}
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={userTagsSaving}
                  on:click={handleSaveUserTags}
                >
                  {userTagsSaving ? $_('messages.saving') : $_('messages.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={userTagsSaving}
                  on:click={handleCancelEditUserTags}
                >
                  {$_('pages.settings.cancel')}
                </Button>
              </div>
            </div>
          {:else}
            <div class="flex flex-wrap gap-1.5 items-center">
              {#if document.userTags.length > 0}
                {#each document.userTags as tag (tag)}
                  <Badge variant="accent">{tag}</Badge>
                {/each}
              {:else}
                <p class="text-sm text-text-muted italic">
                  {$_('components.document_detail.no_tags_yet')}
                </p>
              {/if}
              {#if !isBinned}
                <Button variant="ghost" size="sm" on:click={handleEditUserTags}>
                  {$_('components.document_detail.edit_tags')}
                </Button>
              {/if}
            </div>
          {/if}
        </Card>

        <!-- Assigned To -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <Users class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('components.document_detail.assigned_to')}
          </h2>
          {#if editingAssignedTo}
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-2">
                {#each currentHouseholdMembers as member (member.id)}
                  <label class="flex items-center gap-2 text-sm text-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editableAssignedTo.includes(member.id)}
                      on:change={() => toggleMemberAssignment(member.id)}
                      class="rounded border-border text-accent focus:ring-accent/30"
                    />
                    {member.name}
                  </label>
                {/each}
                {#if currentHouseholdMembers.length === 0}
                  <p class="text-sm text-text-muted italic">{$_('upload.detail.no_members')}</p>
                {/if}
              </div>
              <div class="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={assignedToSaving}
                  on:click={handleSaveAssignedTo}
                >
                  {assignedToSaving ? $_('messages.saving') : $_('messages.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={assignedToSaving}
                  on:click={handleCancelEditAssignedTo}
                >
                  {$_('pages.settings.cancel')}
                </Button>
              </div>
            </div>
          {:else}
            <div class="flex flex-wrap gap-1.5 items-center">
              {#if document.assignedTo.length > 0}
                {#each document.assignedTo as person (person)}
                  <Badge variant="success">{resolveMemberName(person)}</Badge>
                {/each}
              {:else}
                <p class="text-sm text-text-muted italic">
                  {$_('components.document_detail.no_members_assigned')}
                </p>
              {/if}
              {#if !isBinned}
                <Button variant="ghost" size="sm" on:click={handleEditAssignedTo}>
                  {$_('components.document_detail.edit_assigned_to')}
                </Button>
              {/if}
            </div>
          {/if}
        </Card>

        <!-- Notes -->
        <Card>
          <h2 class="flex items-center gap-2 text-sm font-semibold text-text mb-3">
            <StickyNote class="w-4 h-4 text-accent" strokeWidth={1.5} />
            {$_('components.document_detail.notes')}
          </h2>
          {#if editingNotes}
            <div class="flex flex-col gap-3">
              <textarea
                bind:value={editableNotes}
                placeholder={$_('components.document_detail.notes_placeholder')}
                rows="3"
                maxlength="10000"
                class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 resize-y"
              ></textarea>
              <div class="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={notesSaving}
                  on:click={handleSaveNotes}
                >
                  {notesSaving ? $_('messages.saving') : $_('messages.save')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={notesSaving}
                  on:click={handleCancelEditNotes}
                >
                  {$_('pages.settings.cancel')}
                </Button>
              </div>
            </div>
          {:else}
            <div class="flex items-start gap-2">
              {#if document.notes}
                <p class="text-sm text-text whitespace-pre-wrap flex-1">{document.notes}</p>
              {:else}
                <p class="text-sm text-text-muted italic flex-1">
                  {$_('components.document_detail.notes_placeholder')}
                </p>
              {/if}
              {#if !isBinned}
                <Button variant="ghost" size="sm" on:click={handleEditNotes}>
                  <Pencil class="w-3 h-3" strokeWidth={1.5} />
                </Button>
              {/if}
            </div>
          {/if}
        </Card>

        <!-- Extracted Data -->
        {#if document.extractedData}
          <Card>
            <div
              class="flex items-center justify-between mb-3"
              data-testid="doc-detail-extracted-data"
            >
              <button
                class="flex items-center gap-2 text-left text-sm font-semibold text-text hover:text-accent transition-colors"
                on:click={() => (extractedDataExpanded = !extractedDataExpanded)}
              >
                {#if extractedDataExpanded}
                  <ChevronDown class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
                {:else}
                  <ChevronRight class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
                {/if}
                <FileText class="w-4 h-4 text-accent" strokeWidth={1.5} />
                {$_('components.document_detail.extracted_data')}
              </button>

              {#if extractedDataExpanded && !editingExtractedData && !isBinned}
                <Button variant="ghost" size="sm" on:click={handleEditExtractedData}>
                  <Pencil class="w-3 h-3 mr-1" strokeWidth={1.5} />
                  {$_('components.document_detail.edit_extracted_data')}
                </Button>
              {/if}
            </div>

            {#if extractedDataExpanded}
              {#if editingExtractedData}
                <!-- ExtractedDataEditor dispatches change with a freshly assigned
                   Record — avoids the in-place-mutation bug where bind:value on
                   object properties doesn't trigger Svelte reactivity. -->
                <div class="flex flex-col gap-3 mt-2">
                  <div class="max-h-96 overflow-y-auto pr-1">
                    <ExtractedDataEditor
                      values={editableExtractedValues}
                      on:change={(e) => {
                        editableExtractedValues = e.detail;
                      }}
                    />
                  </div>
                  <div class="flex gap-2 pt-2 border-t border-border">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={extractedDataSaving}
                      on:click={handleSaveExtractedData}
                    >
                      {extractedDataSaving ? $_('messages.saving') : $_('messages.save')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={extractedDataSaving}
                      on:click={handleCancelEditExtractedData}
                    >
                      {$_('pages.settings.cancel')}
                    </Button>
                  </div>
                </div>
              {:else}
                <div class="mt-2 space-y-1.5">
                  {#each Object.entries(document.extractedData.extractedValues) as [key, value] (key)}
                    <div
                      class="grid grid-cols-2 gap-4 py-1 border-b border-border/30 last:border-0 items-baseline"
                    >
                      <span class="text-xs font-mono text-text-muted text-right break-words"
                        >{key}</span
                      >
                      <span class="text-xs text-text text-left break-words">{value || '—'}</span>
                    </div>
                  {/each}
                </div>
                <p class="mt-4 text-[10px] text-text-muted uppercase tracking-tighter">
                  Extractor: {document.extractedData.extractorVersion} • {formatDateTime(
                    document.extractedData.extractedAt,
                    $userSettings,
                  )}
                </p>
              {/if}
            {/if}
          </Card>
        {/if}

        <!-- OCR Text -->
        {#if document.ocrText}
          <Card>
            <button
              class="flex items-center gap-2 w-full text-left text-sm font-semibold text-text hover:text-accent transition-colors"
              on:click={() => (ocrExpanded = !ocrExpanded)}
            >
              {#if ocrExpanded}
                <ChevronDown class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
              {:else}
                <ChevronRight class="w-4 h-4 text-text-muted" strokeWidth={1.5} />
              {/if}
              <Eye class="w-4 h-4 text-accent" strokeWidth={1.5} />
              {$_('components.document_detail.ocr_text')}
            </button>
            {#if ocrExpanded}
              <div
                class="mt-3 p-3 bg-bg-alt border border-border rounded text-sm text-text-secondary leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto"
              >
                {document.ocrText}
              </div>
            {/if}
          </Card>
        {/if}

        <!-- References (Related Documents & Reports) -->
        {#if document}
          <DocumentReferences
            documentId={document.id}
            duplicateOf={document.duplicateOf}
            documentCategory={document.classification?.category}
            documentSubCategory={document.classification?.subCategory}
          />
        {/if}
      </div>

      <PreviewSeparator
        bind:width={previewWidth}
        storageKey="plc.documentDetail.previewWidth"
        defaultWidth={600}
        showClass="lg:block"
        testId="doc-detail-preview-separator"
        controls="doc-detail-preview-pane"
      />
      <!-- Preview (right; first on small screens). lg+: a flex column of the row's height; the card
         takes it all and the page area what the caption and pager leave. -->
      <div
        id="doc-detail-preview-pane"
        data-testid="doc-detail-preview-pane"
        style="--preview-width: {previewWidth}px"
        class="order-first lg:order-last lg:w-[var(--preview-width)] shrink-0 flex flex-col"
      >
        <div
          class="flex flex-col flex-1 min-h-0 bg-surface border border-border rounded-lg p-4 text-center"
        >
          <DocumentPreview {document} />
          <p class="mt-3 shrink-0 text-xs text-text-muted">
            {$_('components.document_detail.preview')}
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Feedback Modal -->
  <FeedbackModal
    open={showFeedbackModal}
    documentId={document.id}
    documentCategory={document.classification?.category}
    documentConfidence={document.classification?.confidence}
    householdId={document.householdId}
    on:close={() => (showFeedbackModal = false)}
  />

  <!-- Share Modal (Spec 014 E7) -->
  <Modal
    open={showShareModal}
    title={$_('components.document_detail.share_modal_title')}
    on:close={() => {
      showShareModal = false;
      shareError = null;
    }}
  >
    <!-- Create new link -->
    <div class="flex flex-col gap-3 mb-4">
      <div>
        <label for="share-visibility-select" class="block text-xs font-medium text-text-muted mb-1">
          {$_('components.document_detail.share_visibility_label')}
        </label>
        <select
          id="share-visibility-select"
          bind:value={shareVisibility}
          class="w-full px-3 py-2 bg-bg-alt border border-border rounded text-sm text-text focus:outline-none focus:border-accent"
        >
          <option value="public">{$_('components.document_detail.share_visibility_public')}</option>
          <option value="authenticated"
            >{$_('components.document_detail.share_visibility_authenticated')}</option
          >
        </select>
      </div>
      <div>
        <span class="block text-xs font-medium text-text-muted mb-1">
          {$_('components.document_detail.share_expires_label')}
        </span>
        <div class="flex gap-2 mb-2">
          {#each [['7', 'share_expires_7days'], ['30', 'share_expires_30days'], ['custom', 'share_expires_custom']] as [val, key] (val)}
            <button
              type="button"
              class="px-3 py-1.5 text-xs rounded border transition-colors
                {shareExpiryOption === val
                ? 'bg-accent text-on-accent border-accent'
                : 'bg-bg-alt border-border text-text-secondary hover:border-accent'}"
              on:click={() => {
                shareExpiryOption = val;
              }}
            >
              {$_('components.document_detail.' + key)}
            </button>
          {/each}
        </div>
        {#if shareExpiryOption === 'custom'}
          <DateInput bind:value={shareCustomDate} />
        {/if}
      </div>
      {#if shareError}
        <p class="text-xs text-error">{shareError}</p>
      {/if}
    </div>

    <!-- Active links -->
    <div>
      <h4 class="text-xs font-medium text-text-muted mb-2 flex items-center gap-1.5">
        <Link2 class="w-3.5 h-3.5" strokeWidth={1.5} />
        {$_('components.document_detail.share_active_links')}
      </h4>
      {#if shareLinksLoading}
        <div class="flex items-center gap-2 text-sm text-text-muted">
          <Spinner size="sm" />
        </div>
      {:else if shareLinksError}
        <p class="text-xs text-error">{shareLinksError}</p>
      {:else if shareLinks.length === 0}
        <p class="text-xs text-text-muted">
          {$_('components.document_detail.share_no_active_links')}
        </p>
      {:else}
        <div class="flex flex-col gap-2">
          {#each shareLinks as link (link.id)}
            <div
              class="flex items-center justify-between gap-2 px-2.5 py-2 bg-bg-alt rounded text-xs"
            >
              <div class="flex items-center gap-2 min-w-0">
                <Badge variant="info"
                  >{link.visibility === 'public'
                    ? $_('components.document_detail.share_visibility_public')
                    : $_('components.document_detail.share_visibility_authenticated')}</Badge
                >
                <span class="text-text-muted truncate">
                  {formatDate(String(link.expiresAt), $userSettings)}
                </span>
              </div>
              <div class="flex gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  title={copiedLinkId === link.id
                    ? $_('components.document_detail.share_copied')
                    : $_('components.document_detail.share_copy_link')}
                  aria-label={copiedLinkId === link.id
                    ? $_('components.document_detail.share_copied')
                    : $_('components.document_detail.share_copy_link')}
                  on:click={() => handleCopyShareLink(link)}
                >
                  {#if copiedLinkId === link.id}
                    <CheckCircle class="w-3.5 h-3.5 text-success" strokeWidth={1.5} />
                  {:else}
                    <Copy class="w-3.5 h-3.5" strokeWidth={1.5} />
                  {/if}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  iconOnly
                  disabled={revokingLinkId === link.id}
                  title={$_('components.document_detail.share_revoke')}
                  aria-label={$_('components.document_detail.share_revoke')}
                  on:click={() => handleRevokeShareLink(link)}
                >
                  <Ban class="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <svelte:fragment slot="footer">
      <Button
        variant="primary"
        disabled={shareCreating || (shareExpiryOption === 'custom' && !shareCustomDate)}
        on:click={handleCreateShareLink}
      >
        {shareCreating
          ? $_('components.document_detail.share_creating')
          : $_('components.document_detail.share_create')}
      </Button>
      <Button
        variant="secondary"
        on:click={() => {
          showShareModal = false;
          shareError = null;
        }}
      >
        {$_('pages.settings.cancel')}
      </Button>
    </svelte:fragment>
  </Modal>
{/if}

<!-- Outside the loading/error chain so a refresh of the document keeps the chat window and its draft. -->
{#if chatOpen && chatState !== 'disabled' && chatLockedReason === null && document}
  <DocumentChatPanel
    documentId={document.id}
    displayName={document.displayName || document.fileName}
    contextValues={chatContextValues}
    ocrLength={document.ocrText?.length ?? 0}
    status={chatState}
    availableSkills={chatSkills}
    bind:messages={chatMessages}
    bind:sessionId={chatSessionId}
    activeFollowupDueDate={activeFollowup?.dueDate ?? null}
    bind:sessions={chatSessions}
    on:close={closeChat}
    on:followupCreated={onChatFollowupCreated}
    on:reload={() => chatLoadedFor && loadChatHistory(chatLoadedFor)}
  />
{/if}
